/**
 * 회계구분·회계단위 마스터 — BD DESIGN-006
 *
 * - isBdApiEnabled(): BdAcctClass / BdAcctUnit API
 * - 그 외: localStorage PoC (샘플 seed 유지)
 * - 동기 옵션 헬퍼는 load* 가 채운 메모리 캐시를 읽는다.
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

const ACCT_CLASS_STORAGE_PREFIX = 'budgetAcctClass';
const ACCT_UNIT_STORAGE_PREFIX = 'budgetAcctUnit';
export const ACCOUNTING_MASTER_CHANGED_EVENT = 'erp:accounting-master-changed';

/** 기존 화면 호환용 기본 선택지 — 실제 화면은 기관별 옵션 함수를 사용한다. */
export const ACCT_CLASS_OPTIONS = [
  { value: 'GEN', label: '일반회계' },
  { value: 'SPC', label: '특별회계' },
];
export const ACCT_UNIT_OPTIONS = [
  { value: 'HQ', label: '본점' },
  { value: 'BRANCH', label: '지점' },
];
export const ACCT_CLASS_DEFAULT = 'GEN';
export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

const classCache = new Map();
const unitCache = new Map();

function storageKey(prefix, tenantCode) {
  return `${prefix}|${String(tenantCode || '').trim()}`;
}

export function createAcctUid(prefix = 'AC') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, rows) {
  const saved = (rows || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => ({ ...r, mod: '' }));
  localStorage.setItem(
    key,
    JSON.stringify({ rows: saved, savedAt: new Date().toISOString() })
  );
  return saved;
}

function classKey(tenantCode) {
  return storageKey(ACCT_CLASS_STORAGE_PREFIX, tenantCode);
}

function unitKey(tenantCode) {
  return storageKey(ACCT_UNIT_STORAGE_PREFIX, tenantCode);
}

function emitChanged(tenantCode, axis) {
  window.dispatchEvent(
    new CustomEvent(ACCOUNTING_MASTER_CHANGED_EVENT, {
      detail: { tenantCode, axis },
    })
  );
}

function cachedClasses(tenantCode) {
  return cacheGet(classCache, tenantCode) || [];
}

function cachedUnits(tenantCode) {
  return cacheGet(unitCache, tenantCode) || [];
}

/** 동기 소비처용 — loadAcctUnits 이후 캐시 */
export function getCachedAcctUnits(tenantCode) {
  return cachedUnits(tenantCode);
}

export function getCachedAcctClasses(tenantCode) {
  return cachedClasses(tenantCode);
}

/** 검증용 기저장 스냅샷 — API 모드는 캐시, 로컬은 LS */
function savedClassSnapshot(tenantCode) {
  if (isBdApiEnabled()) return cachedClasses(tenantCode);
  return readRows(classKey(tenantCode));
}

function savedUnitSnapshot(tenantCode) {
  if (isBdApiEnabled()) return cachedUnits(tenantCode);
  return readRows(unitKey(tenantCode));
}

export async function loadAcctClasses(tenantCode) {
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdAcctClass.selectAcctClassList', { tenantCode });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(classCache, tenantCode, mapped);
    return mapped;
  }
  const rows = readRows(classKey(tenantCode));
  if (rows.length) {
    const mapped = rows.map((r) => ({ ...r, mod: '' }));
    cacheSet(classCache, tenantCode, mapped);
    return mapped;
  }
  return saveAcctClasses(tenantCode, [
    {
      acctClassUid: createAcctUid('ACL'),
      acctClassCode: 'GEN',
      acctClassNm: '일반회계',
      defaultYn: 'Y',
      useYn: 'Y',
      sortOrder: 10,
      remark: '기관 기본 회계구분',
    },
    {
      acctClassUid: createAcctUid('ACL'),
      acctClassCode: 'SPC',
      acctClassNm: '특별회계',
      defaultYn: 'N',
      useYn: 'Y',
      sortOrder: 20,
      remark: '',
    },
  ]);
}

export async function saveAcctClasses(tenantCode, rows) {
  const normalized = (rows || []).map((r) => ({
    ...r,
    acctClassUid: r.acctClassUid || createAcctUid('ACL'),
    acctClassCode: String(r.acctClassCode || '').trim(),
    acctClassNm: String(r.acctClassNm || '').trim(),
  }));

  if (isBdApiEnabled()) {
    const dirty = normalized.filter((r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D');
    await commonSaveList(
      [
        'BdAcctClass.insertAcctClass',
        'BdAcctClass.updateAcctClass',
        'BdAcctClass.deleteAcctClass',
      ],
      dirty,
      { tenantCode }
    );
    const mapped = (await commonSelect('BdAcctClass.selectAcctClassList', { tenantCode })).map(
      (r) => ({ ...r, mod: '' })
    );
    cacheSet(classCache, tenantCode, mapped);
    emitChanged(tenantCode, 'class');
    return mapped;
  }

  const saved = writeRows(classKey(tenantCode), normalized);
  cacheSet(classCache, tenantCode, saved);
  emitChanged(tenantCode, 'class');
  return saved;
}

export async function loadAcctUnits(tenantCode) {
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdAcctUnit.selectAcctUnitList', { tenantCode });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(unitCache, tenantCode, mapped);
    return mapped;
  }
  const rows = readRows(unitKey(tenantCode));
  if (rows.length) {
    const mapped = rows.map((r) => ({ ...r, mod: '' }));
    cacheSet(unitCache, tenantCode, mapped);
    return mapped;
  }
  return saveAcctUnits(tenantCode, [
    {
      acctUnitUid: createAcctUid('AUN'),
      acctUnitCode: 'HQ',
      acctUnitNm: '본점',
      useYn: 'Y',
      sortOrder: 10,
      remark: '본부·본점 회계단위',
    },
    {
      acctUnitUid: createAcctUid('AUN'),
      acctUnitCode: 'BRANCH',
      acctUnitNm: '지점',
      useYn: 'Y',
      sortOrder: 20,
      remark: '지점·사업장 회계단위',
    },
  ]);
}

export async function saveAcctUnits(tenantCode, rows) {
  const normalized = (rows || []).map((r) => ({
    ...r,
    acctUnitUid: r.acctUnitUid || createAcctUid('AUN'),
    acctUnitCode: String(r.acctUnitCode || '').trim(),
    acctUnitNm: String(r.acctUnitNm || '').trim(),
  }));

  if (isBdApiEnabled()) {
    const dirty = normalized.filter((r) => r.mod === 'A' || r.mod === 'U' || r.mod === 'D');
    await commonSaveList(
      ['BdAcctUnit.insertAcctUnit', 'BdAcctUnit.updateAcctUnit', 'BdAcctUnit.deleteAcctUnit'],
      dirty,
      { tenantCode }
    );
    const mapped = (await commonSelect('BdAcctUnit.selectAcctUnitList', { tenantCode })).map(
      (r) => ({ ...r, mod: '' })
    );
    cacheSet(unitCache, tenantCode, mapped);
    emitChanged(tenantCode, 'unit');
    return mapped;
  }

  const saved = writeRows(unitKey(tenantCode), normalized);
  cacheSet(unitCache, tenantCode, saved);
  emitChanged(tenantCode, 'unit');
  return saved;
}

export function filterAcctClasses(rows, { acctClassCode, acctClassNm, useYn } = {}) {
  const code = String(acctClassCode || '').trim().toLowerCase();
  const name = String(acctClassNm || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (code && !String(r.acctClassCode || '').toLowerCase().includes(code)) return false;
    if (name && !String(r.acctClassNm || '').toLowerCase().includes(name)) return false;
    if (useYn && r.useYn !== useYn) return false;
    return true;
  });
}

export function filterAcctUnits(rows, { acctUnitCode, acctUnitNm, useYn } = {}) {
  const code = String(acctUnitCode || '').trim().toLowerCase();
  const name = String(acctUnitNm || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (code && !String(r.acctUnitCode || '').toLowerCase().includes(code)) return false;
    if (name && !String(r.acctUnitNm || '').toLowerCase().includes(name)) return false;
    if (useYn && r.useYn !== useYn) return false;
    return true;
  });
}

function validateCode(code, label) {
  if (!code) return `${label} 코드는 필수입니다.`;
  if (!/^[A-Za-z0-9._-]+$/.test(code)) {
    return `${label} 코드 ${code}: 영문·숫자·점·밑줄·하이픈만 사용할 수 있습니다.`;
  }
  return '';
}

export function validateAcctClasses(tenantCode, rows) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const savedByUid = new Map(savedClassSnapshot(tenantCode).map((r) => [r.acctClassUid, r]));
  const codes = new Set();
  let defaultCount = 0;

  for (const row of active) {
    const code = String(row.acctClassCode || '').trim();
    const codeError = validateCode(code, '회계구분');
    if (codeError) return { ok: false, message: codeError };
    const saved = savedByUid.get(row.acctClassUid);
    if (saved && String(saved.acctClassCode || '') !== code) {
      return {
        ok: false,
        message: `저장된 회계구분 코드는 변경할 수 없습니다. (${saved.acctClassCode})`,
      };
    }
    const normalized = code.toLowerCase();
    if (codes.has(normalized)) {
      return { ok: false, message: `회계구분 코드가 중복되었습니다. (${code})` };
    }
    codes.add(normalized);
    if (!String(row.acctClassNm || '').trim()) {
      return { ok: false, message: `회계구분 ${code}: 명칭은 필수입니다.` };
    }
    if (row.useYn === 'Y' && row.defaultYn === 'Y') defaultCount += 1;
  }
  if (!active.some((r) => r.useYn === 'Y')) {
    return { ok: false, message: '사용 중인 회계구분이 최소 1개 필요합니다.' };
  }
  if (defaultCount !== 1) {
    return { ok: false, message: '사용 중인 기본 회계구분은 정확히 1개여야 합니다.' };
  }
  return { ok: true };
}

export function validateAcctUnits(tenantCode, rows) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const savedByUid = new Map(savedUnitSnapshot(tenantCode).map((r) => [r.acctUnitUid, r]));
  const codes = new Set();

  for (const row of active) {
    const code = String(row.acctUnitCode || '').trim();
    const codeError = validateCode(code, '회계단위');
    if (codeError) return { ok: false, message: codeError };
    const saved = savedByUid.get(row.acctUnitUid);
    if (saved && String(saved.acctUnitCode || '') !== code) {
      return {
        ok: false,
        message: `저장된 회계단위 코드는 변경할 수 없습니다. (${saved.acctUnitCode})`,
      };
    }
    const normalized = code.toLowerCase();
    if (codes.has(normalized)) {
      return { ok: false, message: `회계단위 코드가 중복되었습니다. (${code})` };
    }
    codes.add(normalized);
    if (!String(row.acctUnitNm || '').trim()) {
      return { ok: false, message: `회계단위 ${code}: 명칭은 필수입니다.` };
    }
  }
  if (!active.some((r) => r.useYn === 'Y')) {
    return { ok: false, message: '사용 중인 회계단위가 최소 1개 필요합니다.' };
  }
  return { ok: true };
}

export function acctClassOptions(tenantCode, { includeInactive = false } = {}) {
  if (!tenantCode) return ACCT_CLASS_OPTIONS;
  const rows = cachedClasses(tenantCode);
  if (!rows.length) return ACCT_CLASS_OPTIONS;
  return rows
    .filter((r) => includeInactive || r.useYn === 'Y')
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((r) => ({ value: r.acctClassCode, label: r.acctClassNm }));
}

export function acctUnitOptions(tenantCode, { includeInactive = false } = {}) {
  if (!tenantCode) return ACCT_UNIT_OPTIONS;
  const rows = cachedUnits(tenantCode);
  if (!rows.length) return ACCT_UNIT_OPTIONS;
  return rows
    .filter((r) => includeInactive || r.useYn === 'Y')
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((r) => ({ value: r.acctUnitCode, label: r.acctUnitNm }));
}

export function defaultAcctClassCode(tenantCode) {
  if (!tenantCode) return ACCT_CLASS_DEFAULT;
  const rows = cachedClasses(tenantCode);
  return (
    rows.find((r) => r.useYn === 'Y' && r.defaultYn === 'Y')?.acctClassCode ||
    rows.find((r) => r.useYn === 'Y')?.acctClassCode ||
    ACCT_CLASS_DEFAULT
  );
}

export function acctClassLabel(code, tenantCode) {
  const options = tenantCode ? acctClassOptions(tenantCode, { includeInactive: true }) : ACCT_CLASS_OPTIONS;
  return options.find((o) => o.value === code)?.label || code || '';
}

export function acctUnitLabel(code, tenantCode) {
  const options = tenantCode ? acctUnitOptions(tenantCode, { includeInactive: true }) : ACCT_UNIT_OPTIONS;
  return options.find((o) => o.value === code)?.label || code || '';
}

/** 라인 회계구분 매칭 — 도입 이전 저장분은 해당 기관 기본 회계구분으로 간주 */
export function matchesAcctClass(row, acctClass, tenantCode) {
  const fallback = defaultAcctClassCode(tenantCode);
  const target = String(acctClass || fallback);
  const rowClass = String(row?.acctClassCode || '').trim() || fallback;
  return rowClass === target;
}
